import { DeleteRecordAlert } from "@/components/admin/scheduler/DeleteRecordAlert";
import { EditTimeForRecordForm } from "@/components/admin/scheduler/EditTimeForRecordForm";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PHONE_NUMBER, PHONE_TEL } from "@/mock";
import booking from "@/shared/api/booking";
import { format } from "date-fns";
import { Trash } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CountryPhoneInput } from "../CountryPhoneInput";

export const formatPhoneNumber = (rawDigits) => {
    if (!rawDigits) return '';

    // Удаляем всё, кроме цифр
    const digits = rawDigits.replace(/\D/g, '');

    if (digits.length === 0) return '';

    let formatted = '+';
    let offset = 0;

    // Код страны (3 цифры)
    formatted += digits.substring(offset, Math.min(offset + 3, digits.length));
    offset += 3;

    // Код оператора (2 цифры)
    if (digits.length > offset) {
        formatted += ' (';
        formatted += digits.substring(offset, Math.min(offset + 2, digits.length));
        offset += 2;
        if (digits.length >= 5) formatted += ')';
    }

    // Первая часть номера (3 цифры)
    if (digits.length > offset) {
        formatted += ' ';
        formatted += digits.substring(offset, Math.min(offset + 3, digits.length));
        offset += 3;
    }

    // Вторая часть номера (2 цифры)
    if (digits.length > offset) {
        formatted += '-';
        formatted += digits.substring(offset, Math.min(offset + 2, digits.length));
        offset += 2;
    }

    // Третья часть номера (2 цифры)
    if (digits.length > offset) {
        formatted += '-';
        formatted += digits.substring(offset, Math.min(offset + 2, digits.length));
    }

    return formatted;
};


export const BookingForm = ({
    dateKey,
    mode,
    name,
    phone,
    comment,
    setName,
    setPhone,
    setComment,
    onSubmit,
    date,
    time,
    id,
    handleBookingChanged,
    disabledTime
}) => {
    const [editMode, setEditMode] = useState(false);

    const [tempName, setTempName] = useState(name);
    const [tempPhone, setTempPhone] = useState(phone);
    const [tempComment, setTempComment] = useState(comment);
    const [slots, setSlots] = useState([])

    const meetDate = date ? <div>Дата встречи: {format(date, "dd.MM.yyyy")}</div> : null
    const meetTime = time ? <div>Время встречи: {time}</div> : null

    const handleEditClick = e => {
        e.preventDefault();
        setEditMode(true);
        setTempName(name);
        setTempPhone(phone);
        setTempComment(comment);
    }
    const handleEditCancel = e => {
        e.preventDefault();
        setEditMode(false);
        setTempName(name);
        setTempPhone(phone);
        setTempComment(comment);
    }


    useEffect(() => {
        if (!dateKey) return

        const fetchSlots = async () => {
            try {
                const res = await fetch(process.env.REACT_APP_BACKEND_URL + `/api/booking/slots?date=${dateKey}`)
                if (!res.ok) throw new Error("Ошибка загрузки слотов")
                const data = await res.json()
                setSlots(data.slots)
            } catch (err) {
                toast.error(err.message || "Ошибка сервера")
            }
        }

        fetchSlots()
    }, [dateKey])

    const handleSave = async (e) => {
        e.preventDefault();

        try {
            const res = await booking.changeBooking(id, { name: tempName, phone: "+" + tempPhone, comment: tempComment });
            if (res) {
                toast.success("Запись успешно отредактирована");
                handleBookingChanged(prev => {
                    const dateKey = format(date, "yyyy-MM-dd");
                    const day = prev.get(dateKey);
                    if (!day?.length) return prev;

                    // Создаём новый массив с заменой конкретного элемента
                    const updatedDay = day.map(b =>
                        b._id === id
                            ? { ...b, name: tempName, phone: tempPhone, comment: tempComment }
                            : b
                    );

                    // Создаём новый Map (иначе React может не заметить изменений)
                    const newMap = new Map(prev);
                    newMap.set(dateKey, updatedDay);

                    return newMap;
                });

                setName(tempName);
                setPhone(tempPhone);
                setComment(tempComment);
                setEditMode(false);
            } else {
                toast.error("Ошибка при редактировании записи");
            }
        } catch (error) {
            toast.error("Ошибка при редактировании записи");
            console.error(error);
        }
    }

    const baseSubmitStyle = "bg-olive-50 text-olive-800 duration-300 hover:scale-105 border rounded-2xl hover:text-olive-900 hover:bg-olive-700"

    const controlButton = () => {
        if ((mode === "edit" || mode === "phoneEdit") && editMode) return (
            <div className="flex gap-2">
                <Button variant="outline" onClick={handleSave} className={baseSubmitStyle}>
                    Сохранить
                </Button>
                <Button variant="outline" onClick={handleEditCancel} className={baseSubmitStyle}>
                    Отменить
                </Button>
            </div>
        )
        if (mode === "edit" || mode === "phoneEdit") {
            return <Button onClick={handleEditClick} className={baseSubmitStyle}>
                Редактировать
            </Button>
        } else if (mode === "admin") {
            return <Button type="submit" className={baseSubmitStyle}>
                Сохранить
            </Button>
        } else {
            return <Button type="submit" className={baseSubmitStyle}>
                Подтвердить запись
            </Button>
        }
    }

    return (
        <form onSubmit={onSubmit} className="space-y-4 h-full">
            <Card className="bg-white/5 backdrop-blur-sm border-olive-100 h-full">
                <CardHeader>
                    <CardTitle className="font-serif text-olive-950">Время встречи</CardTitle>
                    <CardDescription className="mt-2">{meetDate}{meetTime}</CardDescription>
                </CardHeader>
                <CardContent>

                    <div className="grid sm:grid-cols-2 gap-3">
                        <Input
                            placeholder="Ваше имя"
                            disabled={(mode === 'edit' || mode === 'phoneEdit') && !editMode}
                            required
                            value={editMode ? tempName : name}
                            onChange={e => editMode ? setTempName(e.target.value) : setName(e.target.value)}
                        />
                        {/* <Phone
                            type="tel"
                            placeholder="375 (xx) xxx-xx-xx"
                            disabled={(mode === 'edit' || mode === 'phoneEdit') && !editMode}
                            required
                            value={editMode ? formatPhoneNumber(tempPhone) : formatPhoneNumber(phone)} // Отображаем отформатированный номер из состояния
                            onChange={e => {
                                const inputValue = e.target.value;
                                // Очищаем введенное значение, чтобы получить только цифры для состояния
                                let cleanedDigits = inputValue.replace(/\D/g, '');

                                // Применяем логику преобразования 8/80 в 375 здесь
                                if (cleanedDigits.startsWith('80')) {
                                    cleanedDigits = '375' + cleanedDigits.substring(2);
                                } else if (cleanedDigits.startsWith('8')) {
                                    cleanedDigits = '375' + cleanedDigits.substring(1);
                                }

                                // Ограничиваем до 12 цифр (3 для кода страны, 9 для номера)
                                cleanedDigits = cleanedDigits.substring(0, 12);
                                if (editMode) setTempPhone(cleanedDigits);
                                else setPhone(cleanedDigits); // Обновляем состояние "чистыми" цифрами
                            }}
                        /> */}
                        <CountryPhoneInput
                            value={editMode ? tempPhone : phone}
                            placeholder="375 (xx) xxx-xx-xx"
                            disabled={(mode === 'edit' || mode === 'phoneEdit') && !editMode}
                            required
                            onChange={(e) => {
                                if (editMode) setTempPhone(e);
                                else setPhone(e)
                            }} />
                        <Textarea
                            placeholder="Комментарий (необязательно)"
                            className="sm:col-span-2"
                            value={editMode ? tempComment : comment}
                            onChange={e => editMode ? setTempComment(e.target.value) : setComment(e.target.value)}
                            disabled={(mode === 'edit' || mode === 'phoneEdit') && !editMode}
                        />
                    </div>

                    {(mode === 'edit' || mode === 'phoneEdit') && (
                        <div className="mt-4 flex items-center gap-3">
                            <EditTimeForRecordForm
                                disabledTime={[...(disabledTime ?? []), ...slots.filter(el => !el.available).map(el => el.time)]}
                                currentTime={time}
                                name={name}
                                id={id}
                                handleBookingChanged={handleBookingChanged}
                            />
                        </div>
                    )}


                    <div className="mt-4 flex flex-wrap items-center gap-3">

                        {controlButton()}

                        {(mode === 'edit' || mode === 'phoneEdit') && <DeleteRecordAlert id={id} handleBookingChanged={handleBookingChanged} />}
                        {mode !== 'admin' && mode !== 'edit' && mode !== 'phoneEdit' && <a href={PHONE_TEL} className="text-olive-900/70 hover:text-olive-900 text-sm">
                            Или позвоните: {PHONE_NUMBER}
                        </a>
                        }
                    </div>
                </CardContent>
            </Card>
        </form >
    )
}